import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  List,
  ListItem,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { usePlanning } from '../context/PlanningContext';
import type { Progetto, ProgettoInput } from '../types';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ProjectForm from './ProjectForm';
import BudgetProgressBar from './BudgetProgressBar';
import GruppoProgettiListItem from './GruppoProgettiListItem';
import { GruppoProgetti } from '../types/gruppo';
import * as api from '../services/api';

interface DraggableProjectProps {
  progetto: Progetto;
  onEdit: () => void;
  onDelete: () => void;
}

const DraggableProject: React.FC<DraggableProjectProps> = ({ progetto, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `progetto-${progetto.id}`,
    data: { progetto },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'primary.main',
        },
        '&:hover .project-actions': {
          opacity: 1,
        },
        position: 'relative',
      }}
    >
      <Box {...listeners} {...attributes} sx={{ flex: 1, display: 'flex', cursor: isDragging ? 'grabbing' : 'grab', minWidth: 0 }}>
        <FolderIcon sx={{ mr: 2, color: 'secondary.main', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2" noWrap>
            {progetto.titolo}
          </Typography>
          {progetto.id_hrp && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              ID HRP: {progetto.id_hrp}
            </Typography>
          )}
          {progetto.pm && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              PM: {progetto.pm}
            </Typography>
          )}
          {progetto.cliente && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              Cliente: {progetto.cliente}
            </Typography>
          )}
          {progetto.cliente_finale && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              Cliente Finale: {progetto.cliente_finale}
            </Typography>
          )}

          {/* Budget Progress Bar */}
          {progetto.budget_gg_uu !== undefined && progetto.budget_gg_uu !== null && progetto.budget_gg_uu > 0 && (
            <BudgetProgressBar progetto={progetto} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
        {/* Rimosso il vecchio Chip budget_residuo_gg_uu - ora sostituito da BudgetProgressBar */}

        <Box
          className="project-actions"
          sx={{
            display: 'flex',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
          <Tooltip title="Modifica">
            <IconButton size="small" onClick={onEdit} sx={{ color: 'primary.main' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina">
            <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </ListItem>
  );
};

const ProjectList: React.FC = () => {
  const { refreshProgetti } = usePlanning();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [showDisabled, setShowDisabled] = useState<'enabled' | 'all' | 'gruppi'>('enabled');
  const [selectedGruppo, setSelectedGruppo] = useState<GruppoProgetti | null>(null);
  const [progetti, setProgetti] = useState<Progetto[]>([]);
  const [gruppi, setGruppi] = useState<GruppoProgetti[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingProgetto, setEditingProgetto] = useState<Progetto | null>(null);

  // Ref per Intersection Observer
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 50;

  // Load gruppi progetti
  const loadGruppi = useCallback(async () => {
    try {
      const gruppi = await api.getGruppiProgetti();
      setGruppi(gruppi);
    } catch (error) {
      console.error('Error loading gruppi:', error);
    }
  }, []);

  // Load gruppi on mount
  useEffect(() => {
    loadGruppi();
  }, [loadGruppi]);

  // Load progetti con paginazione e ricerca server-side
  const loadProgetti = useCallback(async (pageNum: number, searchQuery: string, reset: boolean = false) => {
    if (showDisabled === 'gruppi') return;

    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await api.getProgetti(
        showDisabled === 'all',
        pageNum,
        ITEMS_PER_PAGE,
        searchQuery
      );

      if (reset) {
        setProgetti(response.data);
      } else {
        setProgetti(prev => [...prev, ...response.data]);
      }

      setTotalCount(response.pagination.totalCount);
      setHasMore(response.data.length === ITEMS_PER_PAGE && response.pagination.page < response.pagination.totalPages);
    } catch (error) {
      console.error('Error loading progetti:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [showDisabled]);

  // Debounced search: aggiorna searchTerm dopo 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
      setProgetti([]);
      loadProgetti(1, searchInput, true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, loadProgetti]);

  // Reset quando cambia showDisabled
  useEffect(() => {
    setPage(1);
    setProgetti([]);
    loadProgetti(1, searchTerm, true);
  }, [showDisabled, searchTerm, loadProgetti]);

  // Intersection Observer per infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadProgetti(nextPage, searchTerm, false);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loading, loadingMore, page, searchTerm, loadProgetti]);

  const handleAdd = () => {
    setEditingProgetto(null);
    setFormOpen(true);
  };

  const handleEdit = (progetto: Progetto) => {
    setEditingProgetto(progetto);
    setFormOpen(true);
  };

  const handleDelete = async (progetto: Progetto) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il progetto "${progetto.titolo}"?`)) {
      return;
    }

    try {
      await api.deleteProgetto(progetto.id);
      // Reset e ricarica dall'inizio
      setPage(1);
      setProgetti([]);
      await loadProgetti(1, searchTerm, true);
      await refreshProgetti(); // Aggiorna anche il context
    } catch (error: any) {
      alert(error.response?.data?.error || 'Errore nell\'eliminazione del progetto');
    }
  };

  const handleSave = async (progettoData: ProgettoInput) => {
    if (editingProgetto) {
      // Update
      await api.updateProgetto(editingProgetto.id, progettoData);
    } else {
      // Create
      await api.createProgetto(progettoData);
    }
    // Reset e ricarica dall'inizio
    setPage(1);
    setProgetti([]);
    await loadProgetti(1, searchTerm, true);
    await refreshProgetti(); // Aggiorna anche il context
  };

  // Filtri client-side per modalità GRUPPI
  const gruppiFiltered = gruppi.filter(g =>
    g.nome.toLowerCase().includes(searchInput.toLowerCase())
  );
  const gruppiProgettiFiltered = selectedGruppo
    ? selectedGruppo.progetti.filter(p =>
        p.titolo.toLowerCase().includes(searchInput.toLowerCase()) ||
        (p.id_hrp?.toString() || '').includes(searchInput)
      )
    : [];

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Progetti
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Nuovo
          </Button>
        </Box>

        {/* Casella di ricerca con debouncing */}
        <TextField
          size="small"
          placeholder={
            showDisabled === 'gruppi'
              ? selectedGruppo
                ? `Cerca in ${selectedGruppo.nome}...`
                : 'Cerca gruppi...'
              : 'Cerca progetti su tutti i dati...'
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
          helperText={
            showDisabled === 'gruppi'
              ? selectedGruppo
                ? `${gruppiProgettiFiltered.length} progetti nel gruppo`
                : `${gruppiFiltered.length} gruppi`
              : totalCount > 0
              ? `${totalCount} progetti trovati`
              : ''
          }
        />

        {/* Filtro abilitati/tutti/gruppi */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Mostra:
          </Typography>
          <ToggleButtonGroup
            value={showDisabled}
            exclusive
            onChange={(_, value) => {
              if (!value) return;
              if (value !== 'gruppi') setSelectedGruppo(null);
              setShowDisabled(value);
            }}
            size="small"
          >
            <ToggleButton value="enabled">Solo Abilitati</ToggleButton>
            <ToggleButton value="all">Tutti</ToggleButton>
            <ToggleButton value="gruppi">Gruppi</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Trascina un progetto o un gruppo in un mese
        </Typography>

        {/* Lista — modalità GRUPPI */}
        {showDisabled === 'gruppi' ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {selectedGruppo ? (
              /* Vista progetti del gruppo selezionato */
              <>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => { setSelectedGruppo(null); setSearchInput(''); }}
                  sx={{ mb: 1, fontWeight: 'bold' }}
                >
                  {selectedGruppo.nome}
                </Button>
                <List>
                  {gruppiProgettiFiltered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchInput ? 'Nessun progetto trovato' : 'Il gruppo non contiene progetti'}
                      </Typography>
                    </Box>
                  ) : (
                    gruppiProgettiFiltered.map((progetto) => (
                      <DraggableProject
                        key={progetto.id}
                        progetto={progetto}
                        onEdit={() => handleEdit(progetto)}
                        onDelete={() => handleDelete(progetto)}
                      />
                    ))
                  )}
                </List>
              </>
            ) : (
              /* Vista lista gruppi */
              <List>
                {gruppiFiltered.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchInput ? 'Nessun gruppo trovato' : 'Nessun gruppo disponibile'}
                    </Typography>
                  </Box>
                ) : (
                  gruppiFiltered.map((gruppo) => (
                    <GruppoProgettiListItem
                      key={`gruppo-${gruppo.id}`}
                      gruppo={gruppo}
                      onClick={() => { setSelectedGruppo(gruppo); setSearchInput(''); }}
                    />
                  ))
                )}
              </List>
            )}
          </Box>
        ) : (
          /* Lista gruppi e progetti con infinite scroll (modalità normale) */
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading && progetti.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Caricamento...
                </Typography>
              </Box>
            ) : (
              <List>
                {/* Gruppi Progetti */}
                {gruppi.length > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ display: 'block', fontWeight: 'bold', mb: 1, px: 2 }}
                    >
                      GRUPPI PROGETTI
                    </Typography>
                    {gruppi.map((gruppo) => (
                      <GruppoProgettiListItem key={`gruppo-${gruppo.id}`} gruppo={gruppo} />
                    ))}
                    <Box sx={{ my: 2, borderBottom: '2px solid', borderColor: 'divider' }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontWeight: 'bold', mb: 1, px: 2 }}
                    >
                      PROGETTI SINGOLI
                    </Typography>
                  </>
                )}

                {progetti.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchInput ? 'Nessun progetto trovato' : 'Nessun progetto disponibile'}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {progetti.map((progetto) => (
                      <DraggableProject
                        key={progetto.id}
                        progetto={progetto}
                        onEdit={() => handleEdit(progetto)}
                        onDelete={() => handleDelete(progetto)}
                      />
                    ))}

                    {/* Elemento per Intersection Observer */}
                    {hasMore && (
                      <Box ref={loadMoreRef} sx={{ textAlign: 'center', py: 2 }}>
                        {loadingMore && <CircularProgress size={20} />}
                      </Box>
                    )}

                    {/* Messaggio fine lista */}
                    {!hasMore && progetti.length > 0 && (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Fine della lista ({totalCount} progetti)
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </List>
            )}
          </Box>
        )}
      </Paper>

      {/* Form Dialog */}
      <ProjectForm
        open={formOpen}
        progetto={editingProgetto}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />
    </>
  );
};

export default ProjectList;
